import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuthSession } from '@/features/auth/useAuthSession';
import {
  clearReferralCode,
  deviceId,
  storedReferralCode,
} from '@/features/referrals/referral-code';
import { track } from '@/services/analytics';
import {
  createGeneration,
  type CreateGenerationInput,
  type CreateGenerationResult,
} from '@/services/generate';

/**
 * Создание портрета или фотосессии.
 *
 * Кредиты и лимиты пересчитывает сервер — здесь ничего не списывается и
 * заранее не проверяется. Единственное клиентское условие — наличие сессии:
 * без неё запрос всё равно вернёт AUTH_ERROR, но сообщение об этом
 * пользователю понятнее сформулировать сразу.
 */
export function useCreateGenerationMutation() {
  const { session } = useAuthSession();
  const queryClient = useQueryClient();

  return useMutation<CreateGenerationResult, Error, CreateGenerationInput>({
    onMutate: (input) => {
      track('generation_started', {
        collection: input.collectionSlug,
        size: input.selections.photoshootSize,
      });
    },
    mutationFn: (input) => {
      if (!session) {
        return Promise.reject(new Error('Нет активной сессии. Обновите страницу.'));
      }

      // Код приглашения и идентификатор устройства подмешиваются здесь, а
      // не в сервисе: сервис не должен знать про localStorage. Сервер
      // использует их только при первом обращении нового пользователя.
      const referralCode = storedReferralCode();

      return createGeneration(
        {
          ...input,
          ...(referralCode ? { referralCode } : {}),
          deviceHash: deviceId(),
        },
        session.access_token,
      );
    },
    onSuccess: () => {
      // История пополнилась новой работой, а кредитов стало меньше —
      // оба кэша устарели. Сколько именно списалось, знает сервер.
      void queryClient.invalidateQueries({ queryKey: ['history'] });
      void queryClient.invalidateQueries({ queryKey: ['credits'] });
      // Реферал уже заведён сервером при первом обращении — код больше
      // ничего не решает и незачем таскать его дальше.
      clearReferralCode();
    },
  });
}
